'use client';

import { useDevMode } from '@/hooks/useDevMode';
import { useEffect, useState } from 'react';

/**
 * Dev-mode floating badge showing the current release tag.
 *
 * Fetches from /api/release-tag (force-dynamic) instead of taking the value
 * as a server-rendered prop, because the parent layout is shared with SSG'd
 * marketing routes — a server-side `process.env.RELEASE_TAG` read in the
 * layout would bake the build-time value into prerendered HTML (#3523). The
 * route handler always runs at request time, so the badge stays correct
 * across both static and dynamic routes.
 */
export default function ReleaseTag() {
  const isDevMode = useDevMode();
  const [tag, setTag] = useState('unknown');

  useEffect(() => {
    if (!isDevMode) return;
    let cancelled = false;
    fetch('/api/release-tag')
      .then((r) => r.json())
      .then((j: { tag?: string }) => {
        if (!cancelled && typeof j.tag === 'string') setTag(j.tag);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isDevMode]);

  if (!isDevMode) return null;

  return (
    <span
      className='fixed bottom-14 right-6 z-50 rounded-full bg-surface-card px-2.5 py-1 text-[10px] font-medium text-content-muted border border-line-default shadow-sm'
      aria-label={`Release ${tag}`}
      title={`Release: ${tag}`}
    >
      {tag}
    </span>
  );
}
